import type { RecurrenceDefinition as CachedRecurrenceDefinition } from 'redux/slices/recurrenceDefinitionsSlice';

import { getRecurrenceDefinition, type RecurrenceDefinition } from '../endpoints/getRecurrenceDefinition';
import { loadRecurrenceDefinitionById } from './loadRecurrenceDefinitionById';
import type { RecurrenceDefinitionSummary } from './loadRecurrenceDefinitionsLOV';

/**
 * Resolve the full definition a submit needs: prefer the list cache (already
 * loaded), else fetch by zone/name (from the picked LOV row), else by id. The
 * `by-id` path returns a list row, which structurally satisfies the
 * `RecurrenceDefinition` read shape the submit flow expects.
 */
export const resolveRecurrenceDefinitionForSubmit = async (
  recurrenceDefinitionId: number,
  picked: RecurrenceDefinitionSummary | null,
  cachedDefinitions: readonly CachedRecurrenceDefinition[]
): Promise<RecurrenceDefinition> => {
  const cached = cachedDefinitions.find((definition) => definition.recurrence_definition_id === recurrenceDefinitionId);

  if (cached?.recurrences?.length) {
    return {
      recurrence_definition_id: recurrenceDefinitionId,
      name: cached.name,
      description: cached.description ?? null,
      zone_name: cached.zone_name ?? null,
      resubmit_on_error: cached.resubmit_on_error ?? true,
      recurrences: cached.recurrences,
      // The list cache doesn't carry folder info, and the submit flow only
      // reads recurrences/resubmit off this record — never the folder.
      folder_id: null,
      folder_path: null,
      created_at: cached.created_at ?? '',
      updated_at: cached.updated_at ?? '',
    };
  }

  if (picked?.zone_name && picked?.name) {
    return getRecurrenceDefinition(picked.zone_name, picked.name);
  }

  return loadRecurrenceDefinitionById(recurrenceDefinitionId);
};
